import { reactive } from 'vue';
import { jwtDecode } from 'jwt-decode';
import { request } from './api';
import { getToken } from './token';

export { setToken, getToken } from './token';

interface JwtPayload {
  id: string;
  email: string;
  name?: string;
  exp?: number;
  iat?: number;
}

interface UserInfo {
  email_verified?: boolean;
  phone_verified?: boolean;
  verified?: boolean;
  avatar?: string;
  phone?: string;
  country_code?: string;
  name?: string;
}

interface User {
  id: string;
  name: string;
  email: string;
  phone: string;
  country_code: string;
  avatar: string;
  email_verified: boolean;
  token: string;
}

interface Usage {
  isPro: boolean;
  used: number;
  remaining: number;
  total: number;
}

interface UsageResponse {
  amount?: number;
  balance?: number;
  paid?: number;
  free?: number;
  used?: number;
  total?: number;
  remaining?: number;
  isPro?: boolean;
  subscription_plan?: string | null;
  subscription_status?: string | null;
  subscription_until?: string | null;
  request_id?: string;
}

interface UserInfoResponse {
  email_verified?: boolean;
  phone_verified?: boolean;
  avatar?: string;
  phone?: string;
  country_code?: string;
  name?: string;
}

interface UserPatch {
  name?: string;
  email?: string;
  phone?: string;
  avatar?: string;
}

interface GlobalState {
  userInfo: UserInfo;
  user: User;
  usage: Usage;
  job: Promise<void> | null;
}

export const globalState = reactive<GlobalState>({
  userInfo: {},
  user: {
    id: '',
    name: '',
    email: '',
    phone: '',
    country_code: '',
    avatar: '',
    email_verified: true,
    token: '',
  },
  usage: {
    isPro: false,
    used: 0,
    remaining: 5,
    total: 5,
  },
  job: null,
});

let usagePromise: Promise<void> | null = null;
let lastUsageTime = 0;

export async function refreshUsage() {
  getUser();
  if (!globalState.user.id) return;

  if (usagePromise) return usagePromise;

  const now = Date.now();
  if (now - lastUsageTime < 1000) return;

  usagePromise = (async () => {
    try {
      const data = (await request(`/billing/usage`)) as UsageResponse;
      const total = Number.isFinite(Number(data.total))
        ? Number(data.total)
        : Number(data.amount) || 0;
      const remaining = Number.isFinite(Number(data.remaining))
        ? Number(data.remaining)
        : Number(data.balance) || 0;
      const used = Number.isFinite(Number(data.used))
        ? Number(data.used)
        : Math.max(0, total - remaining);

      globalState.usage.used = used;
      globalState.usage.total = total;
      globalState.usage.remaining = remaining;
      const hasSubscription = Boolean(data.subscription_until);
      globalState.usage.isPro =
        typeof data.isPro === 'boolean'
          ? data.isPro
          : hasSubscription
            ? true
            : Number(data.paid) > 0
              ? true
              : total > 20;
      lastUsageTime = Date.now();
    } catch (e) {
      globalState.usage.used = 0;
      globalState.usage.total = 0;
      throw e;
    } finally {
      usagePromise = null;
    }
  })();

  return usagePromise;
}

export async function refreshUserProfile() {
  if (!getToken()) return;
  const user = (await request(`/user/info`)) as UserInfoResponse;
  globalState.userInfo.email_verified = user.email_verified;
  globalState.userInfo.phone_verified = user.phone_verified;
  globalState.userInfo.verified = user.phone_verified || user.email_verified;
  globalState.userInfo.avatar = user.avatar;
  globalState.userInfo.phone = user.phone;
  globalState.userInfo.country_code = user.country_code;
  globalState.userInfo.name = user.name;
}

export async function getUserAsync() {
  try {
    if (globalState.job) {
      await globalState.job;
    } else {
      globalState.job = refreshUserProfile().then(() => {
        globalState.job = null;
      });
      await globalState.job;
    }
  } catch (e) {
    console.error(e);
  }
  return getUser();
}

export async function updateUser(patch: UserPatch) {
  const data = {
    ...patch,
  };

  const updatedUser = await request(`/user`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });

  if (updatedUser && typeof updatedUser === 'object') {
    const next = updatedUser as Partial<UserInfo>;
    if (typeof next.name === 'string') {
      globalState.userInfo.name = next.name;
      globalState.user.name = next.name;
    }
    if (typeof next.avatar === 'string') {
      globalState.userInfo.avatar = next.avatar;
      globalState.user.avatar = next.avatar;
    }
  }

  return updatedUser;
}

export function getUser() {
  const token = getToken();
  if (!token) {
    globalState.user = {
      id: '',
      name: '',
      email: '',
      phone: '',
      country_code: '',
      avatar: '',
      email_verified: true,
      token: '',
    };
    return null;
  }

  try {
    const decoded = jwtDecode<JwtPayload>(token);
    globalState.user = {
      ...globalState.user,
      id: decoded.id,
      name: decoded.name || '',
      email: decoded.email,
      token: token,
      ...globalState.userInfo,
    };
    return globalState.user;
  } catch (e) {
    console.error(e);
    globalState.user = {
      id: '',
      name: '',
      email: '',
      phone: '',
      country_code: '',
      avatar: '',
      token: '',
      email_verified: true,
    };
    return null;
  }
}
